import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import authAPI from '@/api/auth'
import { fetchCurrentUser } from './authSlice'
import { exitLandingPath, selectImpersonation } from './impersonation'
import { IMPERSONATION_EXITED } from './impersonationActionTypes'
import { IMPERSONATION_EXIT_FAILED } from './impersonationCopy'

/**
 * The tenant the backend put the admin's session back on, from the exit
 * response's `attributes.restored_tenant: {id, subdomain} | null`. Absent on
 * an older backend.
 *
 * @param {unknown} response
 * @returns {string|null}
 */
export function restoredTenantIdFrom(response) {
  const id = response?.data?.attributes?.restored_tenant?.id
  return id == null || id === '' ? null : String(id)
}

/**
 * Ends the impersonation and reloads the session: `POST
 * /auth/impersonate/exit/`, then IMPERSONATION_EXITED (adopts the restored
 * tenant, drops the entity slices and the query cache), then `/auth/me`.
 *
 * @returns {import('@reduxjs/toolkit').ThunkAction<Promise<{tenantChanged: boolean}>, any, unknown, any>}
 */
export const exitImpersonation = () => async (dispatch, getState) => {
  const pinnedTenantId = selectImpersonation(getState())?.tenant.id ?? null
  const response = await authAPI.exitImpersonation()
  dispatch({ type: IMPERSONATION_EXITED, payload: { restoredTenantId: restoredTenantIdFrom(response) } })
  await dispatch(fetchCurrentUser()).unwrap()
  const tenantId = getState().auth.currentTenantId ?? null
  return { tenantChanged: String(tenantId) !== String(pinnedTenantId) }
}

/**
 * The Exit control's behaviour: runs `exitImpersonation`, then stays on the
 * current route when it still resolves (see `exitLandingPath`) or lands on
 * `/`. A failure leaves the impersonation in place and says so in a toast.
 *
 * Plain state rather than a react-query mutation: ending the impersonation
 * clears the query client, mutation cache included.
 *
 * @returns {{exit: () => Promise<void>, isExiting: boolean}}
 */
export function useExitImpersonation() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isExiting, setIsExiting] = useState(false)
  // The latest location, so an Exit kept by a toast lands from where the
  // viewer is now rather than where they were when the toast opened.
  const locationRef = useRef(location)
  useEffect(() => {
    locationRef.current = location
  }, [location])

  const exit = useCallback(async () => {
    setIsExiting(true)
    try {
      const { tenantChanged } = await dispatch(exitImpersonation())
      navigate(exitLandingPath(locationRef.current, { tenantChanged }), { replace: true })
    } catch (error) {
      console.error('Failed to exit impersonation:', error)
      toast.error(IMPERSONATION_EXIT_FAILED)
    } finally {
      setIsExiting(false)
    }
  }, [dispatch, navigate])

  return { exit, isExiting }
}
